/**
 * Login endpoint helpers — ported from v1 `app/api/auth/userAuth.ts`.
 *
 * All calls now delegate to the canonical ``ApiClient`` (see
 * ``packages/core/src/api/ApiClient.ts``) — same baseURL resolution
 * (``getEnv().apiUrl``), same ``credentials: "include"`` for the BFF
 * cookie, same error mapping. The local ``request<T>`` helper that lived
 * here pre-Phase-5 was removed: keeping two HTTP code-paths in core was a
 * DRY violation and meant only one of them benefited from the
 * refresh-on-401 / 429-RateLimit / 403-session-gate behaviour. Auth
 * endpoints are listed in ``AUTH_ENDPOINTS`` inside
 * ``packages/core/src/api/tokenRefresh.ts`` and therefore skip the
 * refresh-and-retry branch by design — calling them with a stale cookie
 * is the *expected* path.
 *
 * ``AuthApiError`` is re-exported as an alias of ``ApiError`` so external
 * callers that imported it from ``@ando/core/auth`` keep compiling.
 *
 * IMPORTANT — no PKCE on the client. The Zitadel + PKCE dance is server-side:
 * `/auth/login/idp/start` returns the Zitadel `auth_url`; the browser navigates
 * to it; on return Zitadel hits `/api/auth/idp-callback` (Next route handler)
 * which exchanges the code via FastAPI. The SPA NEVER generates code_verifier
 * and NEVER calls a non-existent `/auth/login/idp/exchange`.
 */

// Direct module imports (NOT the api barrel) to avoid a circular dependency:
// ``api/index`` re-exports ``endpoints/auth`` which re-exports this file.
// ``.js`` suffix follows NodeNext convention used across api/ + storage/ —
// apps/web's ``experimental.extensionAlias`` (next.config.ts) maps it
// back to the underlying ``.ts`` source at build time.
import { apiClient } from "../api/ApiClient.js";
import { ApiError } from "../api/errors.js";
import { clearSession } from "./session.js";
import type {
  LoginPasswordResponse,
  LoginStartResponse,
  LoginTotpResponse,
  RegisterResponse,
  UserInfo,
} from "./types.js";

/**
 * Backwards-compatibility alias. Pre-Phase-5 callers used to do
 * ``catch (e) { if (e instanceof AuthApiError) ... }``. ``ApiError`` is
 * thrown by the unified ``ApiClient`` for the same non-2xx cases, so the
 * alias keeps existing instanceof checks working without rewriting them.
 */
export const AuthApiError = ApiError;
export type AuthApiError = ApiError;

// ---------------------------------------------------------------------------
// Multi-step login
// ---------------------------------------------------------------------------

/** Step 1: identify the user by login name (email or username). */
export function startMultiStepLogin(loginName: string): Promise<LoginStartResponse> {
  return apiClient.post<LoginStartResponse>("/auth/login/start", { login_name: loginName });
}

/** Alias kept for parity with v1 imports. */
export const loginStart = startMultiStepLogin;

/** Step 2: check password. May respond with `mfa_required` if a 2FA factor is enrolled. */
export function loginPassword(loginRef: string, password: string): Promise<LoginPasswordResponse> {
  return apiClient.post<LoginPasswordResponse>("/auth/login/password", {
    login_ref: loginRef,
    password,
  });
}

/** Step 2.5 (legacy alias). */
export function loginWithCredentials(
  loginRef: string,
  password: string,
): Promise<LoginPasswordResponse> {
  return loginPassword(loginRef, password);
}

/** Step 3 (TOTP): verify the 6-digit code from the authenticator app. */
export function verifyTotp(loginRef: string, code: string): Promise<LoginTotpResponse> {
  return apiClient.post<LoginTotpResponse>("/auth/login/totp", { login_ref: loginRef, code });
}

/** Alias kept for parity with v1 imports. */
export const loginTotp = verifyTotp;

/** Step 3 (OTP email — TOTP fallback): trigger Zitadel to send the code. */
export function loginOtpEmailStart(loginRef: string): Promise<{ sent: boolean }> {
  return apiClient.post<{ sent: boolean }>("/auth/login/otp_email/start", {
    login_ref: loginRef,
  });
}

/** Step 3 (OTP email): verify the 6-digit code. */
export function loginOtpEmailVerify(
  loginRef: string,
  code: string,
): Promise<LoginTotpResponse> {
  return apiClient.post<LoginTotpResponse>("/auth/login/otp_email/verify", {
    login_ref: loginRef,
    code,
  });
}

// ---------------------------------------------------------------------------
// SSO — start only. The exchange happens server-side in
// `apps/web/app/api/auth/idp-callback/route.ts`.
// ---------------------------------------------------------------------------

/**
 * Start an SSO redirect. Two shapes (mutually exclusive):
 *   - `"google"` / `"microsoft"` — well-known social IdP wired at the
 *     Zitadel instance level.
 *   - `{ idpId }` — arbitrary Zitadel IdP id (used by org-domain discovery).
 *
 * The caller should `window.location.href = auth_url` after the call.
 */
export function startIdpLogin(
  arg: "google" | "microsoft" | { idpId: string },
): Promise<{ auth_url: string }> {
  const body = typeof arg === "string" ? { provider: arg } : { idp_id: arg.idpId };
  return apiClient.post<{ auth_url: string }>("/auth/login/idp/start", body);
}

// ---------------------------------------------------------------------------
// Registration / password reset
// ---------------------------------------------------------------------------

export function registerUser(
  email: string,
  firstName: string,
  lastName: string,
  password: string,
): Promise<RegisterResponse> {
  return apiClient.post<RegisterResponse>("/auth/register", {
    email,
    first_name: firstName,
    last_name: lastName,
    password,
  });
}

export function verifyEmail(userId: string, code: string): Promise<{ verified: boolean }> {
  return apiClient.post<{ verified: boolean }>("/auth/verify-email", {
    user_id: userId,
    code,
  });
}

export function requestPasswordReset(email: string): Promise<{ sent: boolean }> {
  return apiClient.post<{ sent: boolean }>("/auth/password/reset", { email });
}

export function setNewPassword(
  userId: string,
  code: string,
  newPassword: string,
): Promise<{ success: boolean }> {
  return apiClient.post<{ success: boolean }>("/auth/password/set", {
    user_id: userId,
    code,
    new_password: newPassword,
  });
}

// ---------------------------------------------------------------------------
// Current user / logout
// ---------------------------------------------------------------------------

/** Authoritative session check — hits the backend via the BFF. */
export function getCurrentUser(): Promise<UserInfo> {
  return apiClient.get<UserInfo>("/auth/me");
}

/**
 * Log out — call backend to clear httpOnly cookies, then clear `user_info`
 * client-side. The backend `/auth/logout` Set-Cookie response is the ONLY way
 * to clear `ando_session` from the browser.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post<void>("/auth/logout");
  } catch (error) {
    // Best-effort — keep going so we always tear down the local cookie.
    console.warn("[auth/login] backend logout failed:", error);
  }
  clearSession();
}
